import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Button, Input, DatePicker, Space, Spin, Tag, Tabs, Modal, message, Popconfirm, Row, Col, Statistic, Badge, Empty, Divider, Alert, Checkbox } from 'antd';
import { LinkOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined, ReloadOutlined, SendOutlined, CheckCircleOutlined, TeamOutlined, FileExcelOutlined, WarningOutlined, EditOutlined, ClockCircleOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useDeepLinkWhen } from '../utils/deeplink';
import { parseYouTubeLinks } from '../utils/ytlinks';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function ClaimLinks({ user }) {
  const isAdmin = ['admin', 'super_admin', 'manager', 'hr_manager'].includes(user?.role);
  const [companies, setCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [activeCompany, setActiveCompany] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(useDeepLinkWhen().date || dayjs());
  const [bulkText, setBulkText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [stats, setStats] = useState([]);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [forceLinks, setForceLinks] = useState([]);
  const [checking, setChecking] = useState(false);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/claim-links/companies');
      const all = Array.isArray(res.data) ? res.data : [];
      setAllCompanies(all);
      setCompanies(all);
      if (all.length > 0 && !activeCompany) setActiveCompany(all[0].id);
    } catch (err) { console.error(err); }
  };

  const fetchLinks = useCallback(async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const res = await api.get('/claim-links/links', { params: { company_id: activeCompany, date: date.format('YYYY-MM-DD') } });
      setLinks(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [activeCompany, date]);

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/claim-links/stats', { params: { date: date.format('YYYY-MM-DD') } });
      setStats(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  }, [isAdmin, date]);

  useEffect(() => { fetchCompanies(); }, []);
  useEffect(() => { fetchLinks(); }, [fetchLinks]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Check for duplicates when text changes
  const checkDuplicates = async (text) => {
    const rawLinks = parseYouTubeLinks(text);
    if (rawLinks.length === 0 || !activeCompany) {
      setDuplicateWarnings([]);
      setForceLinks([]);
      return;
    }
    setChecking(true);
    try {
      const res = await api.post('/claim-links/check-duplicates', { company_id: activeCompany, links: rawLinks });
      const dups = Array.isArray(res.data?.duplicates) ? res.data.duplicates : [];
      setDuplicateWarnings(dups);
      const stillPresent = new Set(dups.map(d => d.link));
      setForceLinks(prev => prev.filter(l => stillPresent.has(l)));
    } catch (err) { console.error(err); }
    setChecking(false);
  };

  // Debounce duplicate check
  const [checkTimer, setCheckTimer] = useState(null);
  const handleTextChange = (e) => {
    const val = e.target.value;
    if (activeCompany) setBulkText(val);
    if (checkTimer) clearTimeout(checkTimer);
    setCheckTimer(setTimeout(() => checkDuplicates(val), 600));
  };

  const handleSubmitLinks = async () => {
    const rawLinks = parseYouTubeLinks(bulkText);
    if (rawLinks.length === 0) return message.warning('Paste at least one YouTube link');
    setSubmitting(true);
    try {
      const res = await api.post('/claim-links/links', {
        company_id: activeCompany,
        links: rawLinks,
        date: date.format('YYYY-MM-DD'),
        force_links: forceLinks
      });
      const dups = res.data.duplicates || [];
      const readded = res.data.readded || [];
      if (dups.length > 0 || readded.length > 0) {
        const parts = [`${res.data.count} links added`];
        if (readded.length > 0) parts.push(`${readded.length} dobara daali gayi`);
        if (dups.length > 0) parts.push(`${dups.length} duplicate skip`);
        message.warning(parts.join(', '));
      } else {
        message.success(`${res.data.count} links submitted!`);
      }
      setBulkText('');
      setDuplicateWarnings([]);
      setForceLinks([]);
      fetchLinks();
      if (isAdmin) fetchStats();
    } catch (err) { message.error('Failed to submit links'); }
    setSubmitting(false);
  };

  const handleDeleteLink = async (id) => {
    try {
      await api.delete(`/claim-links/links/${id}`);
      message.success('Link deleted');
      fetchLinks();
      if (isAdmin) fetchStats();
    } catch (err) { message.error('Failed'); }
  };

  const handleAddCompany = async () => {
    const name = newCompany.trim();
    if (!name) return message.warning('Enter company name');
    try {
      await api.post('/claim-links/companies', { company_name: name });
      message.success('Company added!');
      setNewCompany('');
      setAddModal(false);
      fetchCompanies();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDeleteCompany = async (id) => {
    try {
      await api.delete(`/claim-links/companies/${id}`);
      message.success('Company deleted');
      if (activeCompany === id) setActiveCompany(null);
      fetchCompanies();
    } catch (err) { message.error('Failed'); }
  };

  const handleCopyLinks = async (companyLinks, fresh) => {
    const linksToCopy = fresh ? companyLinks.filter(l => !l.downloaded_at) : companyLinks;
    if (linksToCopy.length === 0) {
      message.warning(fresh ? 'No new links to copy' : 'No links to copy');
      return;
    }
    const text = linksToCopy.map(l => l.youtube_link).join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    message.success(`${linksToCopy.length} links copied!`);
    try {
      const ids = linksToCopy.map(l => l.id);
      await api.post('/claim-links/mark-copied', { ids });
      fetchLinks();
      fetchStats();
    } catch {}
  };

  const handleDownload = (companyId, batch, fresh) => {
    const token = localStorage.getItem('token');
    const baseUrl = api.defaults.baseURL || '/api';
    let url = `${baseUrl}/claim-links/download?company_id=${companyId}&date=${date.format('YYYY-MM-DD')}&token=${token}`;
    if (batch) url += `&batch=${batch}`;
    if (fresh) url += `&fresh=1`;
    window.open(url, '_blank');
    setTimeout(() => { fetchLinks(); fetchStats(); }, 1500);
  };

  // Links jaise bhi paste ho (markdown, ek line me kai, youtu.be/shorts) —
  // system khud saaf karke standard shakal me count/submit karta hai.
  const cleanLinks = parseYouTubeLinks(bulkText);
  const linkCount = cleanLinks.length;
  const dupLinkSet = new Set(duplicateWarnings.map(d => d.link));
  const forceSet = new Set(forceLinks);
  const newLinkCount = cleanLinks.filter(l => !dupLinkSet.has(l) || forceSet.has(l)).length;

  // One row per duplicate link (a link can match multiple past dates)
  const uniqueDuplicates = Object.values(duplicateWarnings.reduce((acc, d) => {
    if (!acc[d.link]) acc[d.link] = d;
    return acc;
  }, {}));

  const toggleForce = (link, checked) => {
    setForceLinks(prev => checked ? [...new Set([...prev, link])] : prev.filter(l => l !== link));
  };

  // Group links by batch for display
  const batch1Links = links.filter(l => parseInt(l.batch) === 1);
  const batch2Links = links.filter(l => parseInt(l.batch) === 2);

  const renderLinksList = (linksList, batchLabel) => (
    linksList.length === 0 ? null : (
      <div style={{ marginBottom: 12 }}>
        {batchLabel && <Tag color={batchLabel.includes('Before') ? 'green' : 'orange'} style={{ marginBottom: 8, fontSize: 12 }}>{batchLabel} ({linksList.length} links)</Tag>}
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {linksList.map((lnk, i) => (
            <div key={lnk.id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, minWidth: 24 }}>{i + 1}.</Text>
              <a href={lnk.youtube_link} target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, fontSize: 13, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {lnk.youtube_link}
              </a>
              {lnk.created_at && (
                <Tag icon={<ClockCircleOutlined />} color="default" style={{ fontSize: 10 }}>
                  {dayjs(lnk.created_at).format('h:mm A')}
                </Tag>
              )}
              {isAdmin && lnk.full_name && (
                <Tag color="blue" style={{ fontSize: 11 }}>{lnk.full_name || lnk.username}</Tag>
              )}
              {isAdmin && lnk.downloaded_at && (
                <Tag color="green" style={{ fontSize: 10 }}>Downloaded</Tag>
              )}
              <Popconfirm title="Delete this link?" onConfirm={() => handleDeleteLink(lnk.id)}>
                <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
              </Popconfirm>
            </div>
          ))}
        </div>
      </div>
    )
  );

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        <LinkOutlined /> Claim Links
      </Title>

      {/* Date picker + Add company */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <DatePicker value={date} onChange={d => d && setDate(d)} />
          <Button icon={<PlusOutlined />} onClick={() => setAddModal(true)}>New Company</Button>
          {isAdmin && <Button icon={<DeleteOutlined />} danger onClick={() => setManageOpen(true)}>Manage / Delete Companies</Button>}
          <Button icon={<ReloadOutlined />} onClick={() => { fetchLinks(); fetchCompanies(); if (isAdmin) fetchStats(); }}>Refresh</Button>
        </Space>
      </Card>

      {/* Admin Stats */}
      {isAdmin && stats.length > 0 && (
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          {stats.map(s => (
            <Col xs={12} sm={6} key={s.company_id}>
              <Card size="small" style={{ textAlign: 'center', background: s.link_count > 0 ? '#f6ffed' : '#fafafa', border: s.link_count > 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9' }}>
                <Statistic title={s.company_name} value={s.link_count} suffix="links" valueStyle={{ fontSize: 20, color: s.link_count > 0 ? '#52c41a' : '#999' }} />
                <div style={{ fontSize: 11, color: '#888' }}>
                  {s.staff_count} staff
                  {parseInt(s.new_count || 0) > 0 && <Tag color="red" style={{ marginLeft: 4, fontSize: 10 }}>New: {s.new_count}</Tag>}
                </div>
              </Card>
            </Col>
          ))}
          <Col xs={12} sm={6}>
            <Card size="small" style={{ textAlign: 'center', background: '#f0f5ff', border: '1px solid #adc6ff' }}>
              <Statistic title="Total Links" value={stats.reduce((a, s) => a + parseInt(s.link_count || 0), 0)} valueStyle={{ fontSize: 20, color: '#1890ff' }} prefix={<LinkOutlined />} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Company Tabs */}
      {companies.length === 0 ? (
        <Empty description="No companies yet" />
      ) : (
        <Tabs
          activeKey={String(activeCompany)}
          onChange={k => { setActiveCompany(parseInt(k)); setBulkText(''); setDuplicateWarnings([]); setForceLinks([]); }}
          type="card"
          items={companies.map(c => {
            const stat = stats.find(s => s.company_id === c.id);
            const companyLinks = activeCompany === c.id ? links : [];
            const companyBatch1 = companyLinks.filter(l => parseInt(l.batch) === 1);
            const companyBatch2 = companyLinks.filter(l => parseInt(l.batch) === 2);
            return {
              key: String(c.id),
              label: (
                <span>
                  {c.company_name}
                  {stat && parseInt(stat.link_count) > 0 && (
                    <Badge count={stat.link_count} style={{ marginLeft: 6, backgroundColor: '#52c41a' }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  {/* Paste links area */}
                  <Card size="small" style={{ marginBottom: 16 }}>
                    <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                      <SendOutlined /> Paste {c.company_name} Claim Links
                    </Title>
                    <TextArea
                      rows={6}
                      placeholder={`Paste YouTube links here — kaise bhi (ek line me, youtu.be, shorts, extra text ke saath)...\n\nhttps://youtu.be/xxxxxxxxxxx\nhttps://youtube.com/watch?v=...\nhttps://youtube.com/shorts/...`}
                      value={activeCompany === c.id ? bulkText : ''}
                      onChange={handleTextChange}
                      style={{ fontFamily: 'monospace', fontSize: 13 }}
                    />

                    {/* Duplicate warnings — with "add again" option per link */}
                    {uniqueDuplicates.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        style={{ marginTop: 8 }}
                        message={
                          <Space wrap>
                            <span>{uniqueDuplicates.length} link pehle ja chuki hai (last 10 din) — default me skip hongi</span>
                            <Checkbox
                              checked={forceLinks.length === uniqueDuplicates.length}
                              indeterminate={forceLinks.length > 0 && forceLinks.length < uniqueDuplicates.length}
                              onChange={e => setForceLinks(e.target.checked ? uniqueDuplicates.map(d => d.link) : [])}
                            >
                              Sabhi dobara daalo
                            </Checkbox>
                          </Space>
                        }
                        description={
                          <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12 }}>
                            {uniqueDuplicates.map((d, i) => (
                              <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #fff7e6', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Text code style={{ fontSize: 11, wordBreak: 'break-all', flex: 1 }}>{d.link}</Text>
                                <Text type="warning" style={{ fontSize: 11 }}>
                                  {d.is_self
                                    ? `Aapne ${d.link_date ? dayjs(d.link_date).format('DD MMM') + ' ko' : 'pehle hi'} daal di thi`
                                    : `${d.submitted_by} ne ${d.link_date ? dayjs(d.link_date).format('DD MMM') + ' ko' : 'pehle'} daal di thi`}
                                </Text>
                                <Checkbox
                                  checked={forceSet.has(d.link)}
                                  onChange={e => toggleForce(d.link, e.target.checked)}
                                >
                                  <Text style={{ fontSize: 11 }}>Dobara daalo</Text>
                                </Checkbox>
                              </div>
                            ))}
                          </div>
                        }
                      />
                    )}

                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text type="secondary">{linkCount} links ready (auto-cleaned)</Text>
                        {uniqueDuplicates.length > 0 && (
                          <Text style={{ marginLeft: 8, color: '#52c41a', fontWeight: 600 }}>
                            ({newLinkCount} jayengi{forceLinks.length > 0 ? `, ${forceLinks.length} dobara` : ''})
                          </Text>
                        )}
                        {checking && <Spin size="small" style={{ marginLeft: 8 }} />}
                      </div>
                      <Space>
                        {isAdmin && (
                          <>
                            <Button icon={<DownloadOutlined />} onClick={() => handleDownload(c.id, 0, true)} type="primary" size="small"
                              style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                              New Links Excel
                            </Button>
                            <Button icon={<DownloadOutlined />} onClick={() => handleDownload(c.id, 0)} type="default" size="small">
                              All Links Excel
                            </Button>
                            <Button icon={<CopyOutlined />} onClick={() => handleCopyLinks(companyLinks, true)} size="small"
                              style={{ background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}>
                              Copy New Links
                            </Button>
                            <Button icon={<CopyOutlined />} onClick={() => handleCopyLinks(companyLinks, false)} size="small">
                              Copy All Links
                            </Button>
                          </>
                        )}
                        <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleSubmitLinks} loading={submitting}
                          disabled={newLinkCount === 0 && linkCount === 0}>
                          Done — Submit {uniqueDuplicates.length > 0 ? `${newLinkCount}` : linkCount} Links
                        </Button>
                      </Space>
                    </div>
                  </Card>

                  {/* Existing links list */}
                  <Card size="small" title={
                    <span>
                      <LinkOutlined /> {isAdmin ? 'All Staff Links' : 'My Links'} — {date.format('DD MMM YYYY')}
                      <Tag color="blue" style={{ marginLeft: 8 }}>{companyLinks.length} links</Tag>
                    </span>
                  }>
                    {loading ? <Spin /> : companyLinks.length === 0 ? (
                      <Empty description="No links yet for today" />
                    ) : (
                      <div>
                        {companyBatch1.length > 0 && companyBatch2.length > 0 ? (
                          <>
                            {renderLinksList(companyBatch1, 'Before 5 PM')}
                            <Divider style={{ margin: '8px 0' }} />
                            {renderLinksList(companyBatch2, 'After 5 PM')}
                          </>
                        ) : (
                          renderLinksList(companyLinks, null)
                        )}
                      </div>
                    )}
                  </Card>

                  {/* Admin: Download + Delete company */}
                  {isAdmin && (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <Space wrap>
                        <Button icon={<FileExcelOutlined />} type="primary" onClick={() => handleDownload(c.id, 0, true)}
                          style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                          New Links Excel
                        </Button>
                        <Button icon={<FileExcelOutlined />} onClick={() => handleDownload(c.id, 0)}>
                          All Links Excel
                        </Button>
                        <Button icon={<CopyOutlined />} onClick={() => handleCopyLinks(companyLinks, true)}
                          style={{ background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}>
                          Copy New Links
                        </Button>
                        <Button icon={<CopyOutlined />} onClick={() => handleCopyLinks(companyLinks, false)}>
                          Copy All Links
                        </Button>
                      </Space>
                      <Popconfirm title={`Delete ${c.company_name} and all its links?`} onConfirm={() => handleDeleteCompany(c.id)}>
                        <Button danger icon={<DeleteOutlined />}>Delete Company</Button>
                      </Popconfirm>
                    </div>
                  )}
                </div>
              )
            };
          })}
        />
      )}

      {/* Add company modal */}
      <Modal title="Add New Company" open={addModal} onCancel={() => setAddModal(false)} onOk={handleAddCompany}
        okText="Create">
        <Input placeholder="Company name (e.g. Orchard Music)" value={newCompany}
          onChange={e => setNewCompany(e.target.value)} onPressEnter={handleAddCompany} />
      </Modal>

      {/* Manage / delete companies modal (admin) */}
      <Modal title="Manage / Delete Companies" open={manageOpen} onCancel={() => setManageOpen(false)} footer={null}>
        {allCompanies.length === 0 ? <Empty description="No companies" /> : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {allCompanies.map(c => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text>{c.company_name}</Text>
                <Popconfirm title={`Delete "${c.company_name}" aur iski saari links?`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => handleDeleteCompany(c.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
